/**
 * Conteúdo centralizado dos botões de ajuda das páginas.
 *
 * Como editar: muda o texto aqui — sem precisar abrir a página. Mantém o tom
 * humano, curto, sem termos técnicos (HMAC, webhook, prefix, etc).
 *
 * Como adicionar: cria nova entrada em PAGE_HELP, adiciona o id em PageHelpId,
 * e usa <PageHelp id="seu-id" /> na página.
 */

export type PageHelpId =
  | 'dashboard'
  | 'crm-pipeline'
  | 'identities'
  | 'subscriptions'
  | 'charges'
  | 'communications'
  | 'webhook-events'
  | 'provisioning'
  | 'fiscal'
  | 'fiscal-recurring'
  | 'plans'
  | 'saas-products'
  | 'external-plans'
  | 'integrations'
  | 'asaas-accounts'
  | 'reports'
  | 'settings'
  | 'admin-divergence'
  | 'admin-templates'
  | 'admin-dlq'
  | 'admin-suspension-policy';

interface RelatedLink {
  to: string;
  label: string;
}

interface PageHelpContent {
  title: string;
  /** Em 1-2 frases: o que essa página é, em linguagem normal. */
  what: string;
  /** Como funciona no dia-a-dia. Opcional. */
  how?: string;
  /** Links pra páginas que se conectam. Opcional. */
  related?: RelatedLink[];
}

const PAGE_HELP: Record<PageHelpId, PageHelpContent> = {
  dashboard: {
    title: 'Dashboard',
    what: 'Tela inicial com o resumo de tudo: quanto entrou de dinheiro, quantos clientes ativos, cobranças do dia, divergências pendentes.',
    how: 'Os números atualizam sozinhos a cada vez que você abre. É o lugar pra ter a visão geral do negócio sem precisar abrir cada página.',
    related: [
      { to: '/charges', label: 'Cobranças' },
      { to: '/clients', label: 'Identidades' },
      { to: '/reports', label: 'Relatórios' },
    ],
  },

  'crm-pipeline': {
    title: 'Pipeline Comercial',
    what: 'Visão geral do funil de vendas: quanto está em negociação, quantos deals abertos, propostas a vencer e a conversão de leads por origem.',
    how: 'Use os filtros de período e responsável pra recortar tudo de uma vez. Na tabela "a vencer" dá pra renovar (duplicar) ou aceitar uma proposta sem sair daqui.',
    related: [
      { to: '/crm/deals', label: 'Deals' },
      { to: '/crm/proposals', label: 'Propostas' },
      { to: '/crm/leads', label: 'Leads' },
    ],
  },

  identities: {
    title: 'Identidades (Clientes)',
    what: 'Lista de todas as pessoas e empresas que pagam algum SaaS Owl. Cada identidade pode ter mais de um SaaS (ex: alguém com OwlTask e Reservei).',
    how: 'Quando um cliente compra um plano (pelo checkout ou criado manual aqui), ele vira uma identidade. A partir daqui você vê assinaturas, cobranças e histórico dele.',
    related: [
      { to: '/subscriptions', label: 'Assinaturas' },
      { to: '/charges', label: 'Cobranças' },
      { to: '/identities/awaiting-review', label: 'Aguardando revisão' },
    ],
  },

  subscriptions: {
    title: 'Assinaturas',
    what: 'Todos os planos contratados pelos clientes. Aqui você vê quem assina o quê, quando vence, e o status (ativa, em trial, aguardando pagamento, cancelada).',
    how: 'Cada assinatura tem um plano, um ciclo (mensal/anual) e um dia de cobrança. O sistema gera a cobrança automática perto do vencimento e cobra pelo Asaas.',
    related: [
      { to: '/plans', label: 'Planos' },
      { to: '/charges', label: 'Cobranças' },
      { to: '/clients', label: 'Identidades' },
    ],
  },

  charges: {
    title: 'Cobranças',
    what: 'Todos os boletos, Pix e cartões já emitidos pra clientes — pagos, pendentes ou vencidos.',
    how: 'Cobranças nascem de 3 formas: (1) automática pela assinatura quando chega o dia, (2) manual aqui pelo botão "Nova cobrança", ou (3) pelo checkout público quando alguém compra um plano. O Asaas confirma o pagamento e o status atualiza sozinho.',
    related: [
      { to: '/charges/new', label: 'Criar cobrança manual' },
      { to: '/subscriptions', label: 'Assinaturas' },
      { to: '/admin/divergence-alerts', label: 'Divergências' },
    ],
  },

  communications: {
    title: 'Comunicações',
    what: 'Histórico de e-mails e WhatsApps mandados pros clientes — lembretes de vencimento, cobranças vencidas, recibos.',
    how: 'O sistema dispara mensagens sozinho quando: cobrança próxima de vencer, cobrança em atraso, assinatura cancelada. Aqui você vê o que foi enviado e se chegou.',
    related: [
      { to: '/admin/notification-templates', label: 'Editar templates' },
      { to: '/admin/notifications-dlq', label: 'Falhas de entrega' },
    ],
  },

  'webhook-events': {
    title: 'Eventos recebidos',
    what: 'Tudo que o Asaas e os SaaS Owl mandaram pra cá: pagamentos confirmados, notas emitidas, cadastros novos.',
    how: 'Cada evento é uma "mensagem" externa que o sistema processou. Se algo falhar, aparece aqui com o motivo. Útil pra investigar quando uma cobrança não atualizou ou um cliente sumiu.',
    related: [
      { to: '/integrations', label: 'Integrações SaaS' },
      { to: '/admin/divergence-alerts', label: 'Divergências' },
    ],
  },

  provisioning: {
    title: 'Provisionamento',
    what: 'Quando um cliente compra um plano aqui, o Control Center precisa avisar o SaaS (OwlTask, Reservei) que esse cliente agora tem acesso. Essa página mostra cada uma dessas notificações.',
    how: 'Funciona sozinho na maioria dos casos — só vem aqui quando algo falhou e precisa ser reprocessado manualmente.',
    related: [{ to: '/integrations', label: 'Integrações SaaS' }],
  },

  fiscal: {
    title: 'Fiscal (Notas)',
    what: 'Lista de todas as notas fiscais (NFS-e) emitidas pros clientes.',
    how: 'A nota é emitida automaticamente depois que a cobrança é paga (se o plano tiver "emitir nota" ligado). Aqui você vê status, pode reemitir ou cancelar.',
    related: [
      { to: '/fiscal?tab=recorrentes', label: 'NF recorrente' },
      { to: '/charges', label: 'Cobranças' },
    ],
  },

  'fiscal-recurring': {
    title: 'NF Recorrente',
    what: 'Programações de emissão automática de nota fiscal todo mês, mesmo sem haver uma cobrança vinculada.',
    how: 'Útil pra clientes que pagam fora do sistema (transferência manual, contrato fechado) mas você ainda quer emitir a nota mensal pra eles. O sistema gera no dia configurado.',
    related: [{ to: '/fiscal', label: 'Notas emitidas' }],
  },

  plans: {
    title: 'Planos',
    what: 'Os pacotes que os clientes podem assinar (Starter, Growth, Enterprise, etc). Cada plano tem preço, ciclo, recursos inclusos.',
    how: 'Quando você cria um plano aqui, ele fica disponível no checkout público (central.opusowl.com.br/checkout/slug). Pra vincular o plano com um SaaS específico, use "Páginas relacionadas".',
    related: [
      { to: '/catalog/external-plans', label: 'Planos do SaaS' },
      { to: '/catalog/products', label: 'SaaS Products' },
      { to: '/subscriptions', label: 'Assinaturas' },
    ],
  },

  'saas-products': {
    title: 'SaaS Products (Catálogo)',
    what: 'Cada SaaS do portfólio Owl (OwlTask, Reservei, Vizzy, etc) é cadastrado aqui — com nome, URL, status e configuração de integração.',
    how: 'Aqui você decide quais SaaS existem e como o Control Center se conecta com eles. O badge "Conectado/Desconectado" reflete se a integração tá funcionando agora.',
    related: [
      { to: '/integrations', label: 'Status das integrações' },
      { to: '/catalog/external-plans', label: 'Planos do SaaS' },
    ],
  },

  'external-plans': {
    title: 'Planos do SaaS (External Plans)',
    what: 'Cada SaaS tem seus próprios "níveis" de plano (ex: OwlTask tem Nocturne e Omnisight). Aqui você cadastra esses níveis e diz qual plano comercial do CC corresponde a cada um.',
    how: 'Quando um cliente compra um plano do CC, o sistema sabe qual nível liberar no SaaS por causa desse vínculo. Pense como um "tradutor" entre plano comercial e plano técnico do SaaS.',
    related: [
      { to: '/plans', label: 'Planos comerciais' },
      { to: '/catalog/products', label: 'SaaS Products' },
    ],
  },

  integrations: {
    title: 'Integrações SaaS',
    what: 'Visão runtime de quais SaaS estão se comunicando com o Control Center agora.',
    how: 'O sistema bate na URL de cada SaaS a cada 5 minutos pra ver se ele responde. Se responder OK, fica "Conectado". Se cair ou demorar muito, fica "Desconectado". Também tem o botão "Importar histórico" pra trazer dados antigos de um SaaS novo.',
    related: [
      { to: '/catalog/products', label: 'SaaS Products' },
      { to: '/webhook-events', label: 'Eventos recebidos' },
    ],
  },

  'asaas-accounts': {
    title: 'Contas Asaas',
    what: 'As contas do Asaas que o Control Center usa pra cobrar — chave de API, webhook, ambiente (produção/sandbox).',
    how: 'Normalmente é só uma conta "Owl Master" que cobra de todos os SaaS. Se você abrir um CNPJ separado pra algum SaaS, cadastra outra conta aqui e vincula no SaaS Product.',
    related: [
      { to: '/catalog/products', label: 'SaaS Products' },
      { to: '/charges', label: 'Cobranças' },
    ],
  },

  reports: {
    title: 'Relatórios',
    what: 'Visões consolidadas: faturamento por período, MRR (receita recorrente), churn (clientes que saíram), comparativos.',
    how: 'Usa os dados das assinaturas e cobranças. Tem filtros por SaaS, plano e período. Bom pra reuniões e tomada de decisão.',
    related: [
      { to: '/dashboard', label: 'Dashboard' },
      { to: '/subscriptions', label: 'Assinaturas' },
    ],
  },

  settings: {
    title: 'Configurações',
    what: 'Ajustes gerais do Control Center: usuários da equipe, preferências de notificação, integrações globais.',
  },

  'admin-divergence': {
    title: 'Divergências',
    what: 'Sinais de alerta que o sistema detectou e que precisam de atenção humana — não são bugs, são situações que ele não sabe resolver sozinho.',
    how: 'Tipos mais comuns: (1) WhatsApp de número desconhecido — alguém mandou mensagem mas não é cliente cadastrado, você decide se cria/vincula/ignora; (2) Webhook sem resposta — o Asaas parou de mandar eventos por muito tempo, vale checar se tá tudo OK; (3) Cobrança em atraso longo — cliente passou do limite. Use "Ignorar" pra dispensar rápido (spam, falso alarme) ou "Resolver" pra documentar o que você fez.',
    related: [
      { to: '/webhook-events', label: 'Eventos recebidos' },
      { to: '/charges', label: 'Cobranças' },
      { to: '/clients', label: 'Identidades' },
    ],
  },

  'admin-templates': {
    title: 'Templates de Notificação',
    what: 'Os textos prontos dos e-mails e WhatsApps que o sistema dispara automático (lembrete de vencimento, cobrança em atraso, boas-vindas, etc).',
    how: 'Edita o texto aqui — usa variáveis como {{cliente_nome}} ou {{valor}} pra personalizar. Salvou, vale pra todos os envios seguintes.',
    related: [{ to: '/communications', label: 'Mensagens enviadas' }],
  },

  'admin-dlq': {
    title: 'Falhas de Notificação (DLQ)',
    what: 'Mensagens que tentaram ser enviadas e falharam várias vezes (e-mail rejeitado, número de WhatsApp inválido, etc).',
    how: 'Você pode tentar reenviar uma falha individual ou descartar de vez. Útil pra limpar pendências antigas e ver padrões (cliente com e-mail errado, por exemplo).',
    related: [
      { to: '/communications', label: 'Mensagens enviadas' },
      { to: '/admin/notification-templates', label: 'Editar templates' },
    ],
  },

  'admin-suspension-policy': {
    title: 'Política de Suspensão',
    what: 'As regras de quando um cliente inadimplente tem o acesso bloqueado automaticamente — quantos dias depois do vencimento, com quanto aviso prévio.',
    how: 'O botão geral liga/desliga a suspensão automática (desligado por padrão — nada acontece sem você ligar). Cada SaaS tem seu próprio botão, e clientes VIP podem ser isentados na ficha deles.',
    related: [{ to: '/catalog/products', label: 'Produtos SaaS' }],
  },
};

export function getPageHelp(id: PageHelpId): PageHelpContent | null {
  return PAGE_HELP[id] ?? null;
}
